import { FunctionComponent, ReactNode } from 'react';
import { twMerge } from 'src/utils/twMerge';
import StatusBar, {
  Style,
  type StatusBarStyle,
} from 'src/designSystem/StatusBar';
import SafeAreaBottom from 'src/designSystem/SafeArea/SafeAreaBottom';
import SafeAreaTop from 'src/designSystem/SafeArea/SafeAreaTop';
import BackgroundPattern from 'src/designSystem/BackgroundPattern';
import SunburstRays from 'src/designSystem/SunburstRays';
import { PAGE_X } from 'src/designSystem/PageHeader';
import { useIsWebFunnel } from 'src/contexts/WebFunnelContext';
import { projectColors } from 'src/styles/project-styles';
import { hexToRgba } from 'src/utils/hexaToRgba';
import { viewportHeight } from 'src/utils/viewportHeight';

type Variant =
  'light' | 'white' | 'primary' | 'dark' | 'result' | 'resultBlock';

type StatusBarConfig = {
  style: StatusBarStyle;
  color?: string;
  isTransient?: boolean;
};

type Preset = {
  background: string;
  statusBar: StatusBarConfig;
  safeAreaTopClassName?: string;
  hasPattern?: boolean;
  decoration?: ReactNode;
  fillClassName?: string;
  innerClassName?: string;
  innerDecoration?: ReactNode;
};

const getPreset = (variant: Variant): Preset => {
  switch (variant) {
    case 'white':
      return {
        background: 'bg-structural-lightest',
        statusBar: {
          color: projectColors.structural.lightest,
          style: Style.Light,
        },
        hasPattern: true,
      };
    case 'primary':
      return {
        background:
          'bg-gradient-to-t from-paywall-darkest-from to-paywall-darkest-to',
        statusBar: { style: Style.Dark },
        innerClassName:
          'flex z-raised flex-col items-center justify-between w-full h-full',
        innerDecoration: <BackgroundPattern />,
      };
    case 'dark':
      return {
        background: 'bg-structural-darkest',
        statusBar: { style: Style.Dark },
        safeAreaTopClassName: 'bg-structural-darkest',
        fillClassName: 'z-raised w-full h-full max-h-screen supports-[height:100dvh]:max-h-dvh flex flex-col',
        decoration: (
          <BackgroundPattern
            color={hexToRgba(projectColors.structural.darker, 0.2)}
          />
        ),
      };
    case 'result':
    case 'resultBlock':
      return {
        background:
          variant === 'result'
            ? 'bg-gradient-to-b from-paywall-darkest-to to-paywall-darkest-from'
            : 'bg-gradient-to-b from-primary-darkest from-70% to-primary-black',
        statusBar: { style: Style.Dark },
        fillClassName: 'z-raised w-full h-full max-h-screen supports-[height:100dvh]:max-h-dvh flex flex-col relative',
        decoration: <SunburstRays skin="result" className="absolute inset-0" />,
      };
    default:
      return {
        background: 'bg-structural-lightest',
        statusBar: {
          color: projectColors.structural.lightest,
          style: Style.Light,
        },
      };
  }
};

type Props = {
  children?: ReactNode;
  variant?: Variant;
  header?: ReactNode;
  footer?: ReactNode;
  isScrollBody?: boolean;
  fitContent?: boolean;
  withPattern?: boolean;
  isIsolated?: boolean;
  appBackground?: string;
  appBackgroundFooter?: string;
  statusBar?: StatusBarConfig;
  safeAreaTopClassName?: string;
  className?: string;
  'data-testid'?: string;
};

const PageLayout: FunctionComponent<Props> = ({
  children,
  variant = 'light',
  header,
  footer,
  isScrollBody = false,
  fitContent = false,
  withPattern,
  isIsolated = false,
  appBackground,
  appBackgroundFooter,
  statusBar,
  safeAreaTopClassName,
  className,
  'data-testid': dataTestId,
}) => {
  const isWebFunnel = useIsWebFunnel();
  const preset = getPreset(variant);
  const background = appBackground ?? preset.background;
  const isGradientBackground = background.includes('bg-gradient');
  const {
    style: statusBarStyle,
    color: statusBarColor,
    isTransient: isStatusBarTransient,
  } = statusBar ?? preset.statusBar;
  const topStripColor = isGradientBackground ? undefined : statusBarColor;

  const frameTop = (
    <>
      <StatusBar style={statusBarStyle} isTransient={isStatusBarTransient} />
      <SafeAreaTop
        className={
          safeAreaTopClassName ??
          (isGradientBackground ? undefined : preset.safeAreaTopClassName) ??
          (isScrollBody && variant === 'light' && !isGradientBackground
            ? 'bg-structural-lightest'
            : undefined)
        }
        style={
          topStripColor && !safeAreaTopClassName
            ? { backgroundColor: topStripColor }
            : undefined
        }
      />
    </>
  );

  const hasPattern = withPattern ?? preset.hasPattern ?? false;

  if (isScrollBody) {
    const scrollFrame = (
      <>
        {frameTop}
        {header}
        <div className="flex-1 min-w-0 min-h-0 relative">
          <div
            className={twMerge(
              'h-full w-full overflow-hidden overflow-y-auto flex flex-col py-4',
              PAGE_X,
              className,
            )}
          >
            {children}
            {!footer && <SafeAreaBottom className={appBackgroundFooter} />}
          </div>
        </div>
        {footer}
        {footer && <SafeAreaBottom className={appBackgroundFooter} />}
      </>
    );

    return (
      <div
        className={twMerge(
          'w-full h-full max-h-screen supports-[height:100dvh]:max-h-dvh flex flex-col',
          hasPattern && 'relative',
          background,
        )}
        data-testid={dataTestId}
      >
        {hasPattern ? (
          <>
            <BackgroundPattern />
            <div className="z-raised relative flex flex-1 min-h-0 flex-col">
              {scrollFrame}
            </div>
          </>
        ) : (
          scrollFrame
        )}
      </div>
    );
  }

  const scrollableFunnel = isWebFunnel && !fitContent;
  const isFillFrame = Boolean(preset.fillClassName);
  const contentClassName =
    preset.fillClassName ??
    `z-raised w-full flex flex-col relative ${
      fitContent
        ? 'flex-1 min-h-0'
        : scrollableFunnel
          ? 'md:h-full max-md:min-h-full'
          : 'h-full'
    }`;
  const content = (
    <>
      {header}
      {children}
      {footer}
    </>
  );
  return (
    <div
      className={`w-full h-full max-h-screen supports-[height:100dvh]:max-h-dvh flex flex-col relative ${background} ${fitContent ? 'overflow-hidden' : 'overflow-auto'} ${isIsolated ? 'isolate' : ''}`}
      data-testid={dataTestId}
    >
      {frameTop}
      <div
        className={twMerge(contentClassName, className)}
        style={
          isFillFrame || fitContent || scrollableFunnel
            ? undefined
            : { maxHeight: `calc(${viewportHeight(100)} - 12px)` }
        }
      >
        {preset.innerClassName ? (
          <div className={preset.innerClassName}>{content}</div>
        ) : (
          content
        )}
        {preset.innerDecoration}
      </div>
      {preset.decoration ?? (hasPattern ? <BackgroundPattern /> : null)}
      <SafeAreaBottom
        className={
          appBackgroundFooter ?? (isGradientBackground ? undefined : background)
        }
      />
    </div>
  );
};

export default PageLayout;
